import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Tab,
  Tabs,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Checkbox,
  FormControlLabel,
  Switch,
  Chip,
  IconButton,
  Divider,
  Paper,
  Avatar,
  Card,
  CardContent,
  CardActions,
  LinearProgress,
  CircularProgress,
  Badge,
  Tooltip,
  useTheme
} from '@material-ui/core';
import {
  Add as AddIcon,
  Assignment as TaskIcon,
  Comment as CommentIcon,
  AttachFile as AttachIcon,
  AccountTree as SubtaskIcon,
  Repeat as RepeatIcon,
  Event as EventIcon,
  Category as CategoryIcon,
  PriorityHigh as PriorityIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  GetApp as DownloadIcon,
  Image as ImageIcon,
  Description as DocumentIcon,
  VideoLibrary as VideoIcon,
  MusicNote as AudioIcon,
  Folder as ArchiveIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  Warning as WarningIcon,
  WhatsApp as WhatsAppIcon
} from '@material-ui/icons';
import { Check as CheckIcon, Undo as UndoIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import taskService from '../../services/taskService';
import { AuthContext } from '../../context/Auth/AuthContext';
import useUsers from '../../hooks/useUsers';
import Autocomplete from '@material-ui/lab/Autocomplete';

// Função utilitária para mapear categorias com ícones e labels
const getCategoryLabel = (category) => {
  const categoryMap = {
    // 🏢 Departamentais
    'administrativo': '🏢 Administrativo',
    'financeiro': '💰 Financeiro',
    'comercial': '🤝 Comercial',
    'marketing': '📢 Marketing',
    'atendimento': '📞 Atendimento',
    'recursos-humanos': '👥 Recursos Humanos',
    'tecnologia': '💻 Tecnologia',
    'juridico': '⚖️ Jurídico',
    
    // 📋 Atividades
    'reuniao': '🤝 Reunião',
    'projeto': '📋 Projeto',
    'treinamento': '🎓 Treinamento',
    'apresentacao': '📊 Apresentação',
    'documentacao': '📄 Documentação',
    'analise': '🔍 Análise',
    'planejamento': '📅 Planejamento',
    'revisao': '✅ Revisão',
    
    // 🎯 Especiais
    'urgente': '🚨 Urgente',
    'follow-up': '🔄 Follow-up',
    'pessoal': '👤 Pessoal',
    'manutencao': '🔧 Manutenção',
    'auditoria': '🔎 Auditoria',
    'compliance': '📜 Compliance'
  };
  
  return categoryMap[category] || category; // Mantém compatibilidade com categorias antigas
};

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: '1000px',
      width: '95%',
      height: '90vh',
      margin: theme.spacing(1),
      borderRadius: 16,
      overflow: 'hidden',
      background: theme.palette.mode === 'light' 
        ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`
        : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
    },
  },
  // Destaque visual para subtarefa concluída
  completedSubtask: {
    borderLeft: (theme) => `4px solid ${(theme.palette && theme.palette.success && theme.palette.success.main) || '#4caf50'}`,
    backgroundColor: (theme) => (
      (theme.palette && (theme.palette.type === 'dark' || theme.palette.mode === 'dark'))
        ? 'rgba(76,175,80,0.15)'
        : 'rgba(76,175,80,0.08)'
    ),
    boxShadow: (theme) => theme.shadows ? theme.shadows[1] : '0 1px 3px rgba(0,0,0,0.1)'
  },
  dialogTitle: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1.5, 2), // Reduzir padding vertical e horizontal
    minHeight: 'auto', // Permitir altura automática
    '& .MuiTypography-h6': {
      fontWeight: 600,
      fontSize: '1.1rem', // Diminuir tamanho da fonte
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.8) // Diminuir espaçamento entre ícone e texto
    }
  },
  dialogContent: {
    padding: 0,
    overflow: 'hidden',
    height: 'calc(90vh - 140px)', // Altura específica para o conteúdo
    display: 'flex',
    flexDirection: 'column'
  },
  tabsContainer: {
    background: (theme.palette.type === 'dark' || theme.palette.mode === 'dark')
      ? 'rgba(0,0,0,0.3)'
      : '#FFFFFF',
    backdropFilter: 'blur(10px)',
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0, // Não permitir que encolha
    '& .MuiTabs-indicator': {
      height: 3,
      borderRadius: '3px 3px 0 0',
      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
    },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.95rem',
      minHeight: 72,
      '&.Mui-selected': {
        color: theme.palette.primary.main
      }
    }
  },
  tabPanel: {
    padding: theme.spacing(3),
    flex: 1, // Ocupar o espaço restante
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.mode === 'light' 
        ? 'rgba(0,0,0,0.1)' 
        : 'rgba(255,255,255,0.1)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
      borderRadius: '4px',
    },
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.dark}15 100%)`,
    borderRadius: 12,
    border: `1px solid ${theme.palette.primary.main}30`,
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
    fontSize: '1.5rem'
  },
  fieldRow: {
    marginBottom: theme.spacing(3),
  },
  styledTextField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: theme.palette.mode === 'light' 
        ? 'rgba(255,255,255,0.8)' 
        : 'rgba(255,255,255,0.05)',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s ease',
      '&:hover': {
        background: theme.palette.mode === 'light' 
          ? 'rgba(255,255,255,0.9)' 
          : 'rgba(255,255,255,0.08)',
        transform: 'translateY(-1px)',
        boxShadow: `0 4px 20px ${theme.palette.primary.main}20`
      },
      '&.Mui-focused': {
        background: theme.palette.mode === 'light' 
          ? 'rgba(255,255,255,1)' 
          : 'rgba(255,255,255,0.1)',
        boxShadow: `0 6px 25px ${theme.palette.primary.main}30`
      }
    },
    '& .MuiInputLabel-outlined': {
      fontWeight: 500
    }
  },
  styledSelect: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: theme.palette.mode === 'light' 
        ? 'rgba(255,255,255,0.8)' 
        : 'rgba(255,255,255,0.05)',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s ease',
      '&:hover': {
        background: theme.palette.mode === 'light' 
          ? 'rgba(255,255,255,0.9)' 
          : 'rgba(255,255,255,0.08)',
        transform: 'translateY(-1px)',
        boxShadow: `0 4px 20px ${theme.palette.primary.main}20`
      }
    }
  },
  recurrenceSection: {
    background: theme.palette.mode === 'light' 
      ? 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)'
      : 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 100%)',
    backdropFilter: 'blur(10px)',
    padding: theme.spacing(2), // Reduzido de 3 para 2
    borderRadius: 12, // Reduzido de 16 para 12
    marginTop: theme.spacing(2), // Reduzido de 3 para 2
    border: `1px solid ${theme.palette.primary.main}20`,
    boxShadow: `0 4px 20px ${theme.palette.primary.main}10` // Reduzido a sombra
  },
  recurrenceCardSection: {
    background: theme.palette.mode === 'light' 
      ? 'linear-gradient(135deg, rgba(33,150,243,0.1) 0%, rgba(33,150,243,0.05) 100%)'
      : 'linear-gradient(135deg, rgba(33,150,243,0.15) 0%, rgba(33,150,243,0.08) 100%)',
    backdropFilter: 'blur(10px)',
    padding: theme.spacing(2),
    borderRadius: 12,
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.primary.main}20`,
    boxShadow: `0 4px 20px ${theme.palette.primary.main}10`
  },
  reminderSection: {
    background: theme.palette.mode === 'light' 
      ? 'linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0.05) 100%)'
      : 'linear-gradient(135deg, rgba(255,193,7,0.15) 0%, rgba(255,193,7,0.08) 100%)',
    backdropFilter: 'blur(10px)',
    padding: theme.spacing(2),
    borderRadius: 12,
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.warning.main}20`,
    boxShadow: `0 4px 20px ${theme.palette.warning.main}10`
  },
  chipContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  actionButtons: {
    background: (theme.palette.type === 'dark' || theme.palette.mode === 'dark')
      ? 'rgba(0,0,0,0.2)' 
      : 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(10px)',
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
    '& .MuiButton-contained': {
      borderRadius: 25,
      padding: theme.spacing(1, 3),
      textTransform: 'none',
      fontWeight: 600,
      boxShadow: `0 4px 15px ${theme.palette.primary.main}40`,
      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
      '&:hover': {
        boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
        transform: 'translateY(-1px)'
      }
    },
    '& .MuiButton-outlined': {
      borderRadius: 25,
      padding: theme.spacing(1, 3),
      textTransform: 'none',
      fontWeight: 600,
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      '&:hover': {
        background: `${theme.palette.primary.main}15`,
        transform: 'translateY(-1px)'
      }
    }
  },
  styledCard: {
    borderRadius: 16,
    marginBottom: theme.spacing(2),
    background: theme.palette.mode === 'light' 
      ? 'rgba(255,255,255,0.9)' 
      : 'rgba(255,255,255,0.05)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${theme.palette.primary.main}20`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px ${theme.palette.primary.main}25`
    }
  },
  addButton: {
    borderRadius: 25,
    padding: theme.spacing(1.5, 3),
    textTransform: 'none',
    fontWeight: 600,
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: 'white',
    border: 'none',
    boxShadow: `0 4px 15px ${theme.palette.primary.main}40`,
    transition: 'all 0.3s ease',
    '&:hover': {
      background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
      boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
      transform: 'translateY(-1px)'
    }
  },
  uploadArea: {
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: 16,
    padding: theme.spacing(4),
    textAlign: 'center',
    background: `${theme.palette.primary.main}08`,
    marginBottom: theme.spacing(2),
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      background: `${theme.palette.primary.main}15`,
      borderColor: theme.palette.primary.dark,
      transform: 'translateY(-2px)'
    }
  },
  attachmentCard: {
    borderRadius: 12,
    marginBottom: theme.spacing(1),
    background: theme.palette.mode === 'light' 
      ? 'rgba(255,255,255,0.9)' 
      : 'rgba(255,255,255,0.05)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${theme.palette.primary.main}20`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateX(4px)',
      boxShadow: `0 4px 15px ${theme.palette.primary.main}20`
    }
  },
  fileIcon: {
    fontSize: '2rem',
    marginRight: theme.spacing(2)
  },
  commentCard: {
    borderRadius: 12,
    marginBottom: theme.spacing(1),
    background: theme.palette.mode === 'light' 
      ? 'rgba(255,255,255,0.9)' 
      : 'rgba(255,255,255,0.05)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${theme.palette.primary.main}20`,
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: 4,
      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
      borderRadius: '4px 0 0 4px'
    }
  },
  priorityBadge: {
    '&.high': {
      background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
      color: 'white'
    },
    '&.medium': {
      background: 'linear-gradient(45deg, #4ecdc4, #44a08d)',
      color: 'white'
    },
    '&.low': {
      background: 'linear-gradient(45deg, #a8e6cf, #7fcdcd)',
      color: 'white'
    }
  },
  addFormTitle: {
    color: (theme.palette.type === 'dark' || theme.palette.mode === 'dark') ? '#FFFFFF' : theme.palette.primary.main,
  },
  sendCommentButton: {
    borderRadius: 25,
    padding: theme.spacing(1.5, 3),
    textTransform: 'none',
    fontWeight: 600,
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: '#FFFFFF',
    border: 'none',
    boxShadow: `0 4px 15px ${theme.palette.primary.main}40`,
    transition: 'all 0.3s ease',
    marginLeft: theme.spacing(2),
    '&:hover': {
      background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
      boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
      transform: 'translateY(-1px)'
    },
    '&:disabled': {
      background: theme.palette.action.disabled,
      color: theme.palette.action.disabled,
      boxShadow: 'none'
    }
  },
  pendingDeleteCard: {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(244, 67, 54, 0.1) 10px, rgba(244, 67, 54, 0.1) 20px)',
      borderRadius: 'inherit',
      zIndex: 1,
      pointerEvents: 'none'
    }
  },
  pendingDeleteContent: {
    position: 'relative',
    zIndex: 2,
    textDecoration: 'line-through',
    opacity: 0.7,
    filter: 'grayscale(50%)',
    transition: 'all 0.3s ease'
  },
  deleteWarningChip: {
    background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
    color: 'white',
    fontWeight: 600,
    animation: '$pulse 2s infinite',
    '& .MuiChip-icon': {
      color: 'white'
    }
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)'
    },
    '70%': {
      boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)'
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)'
    }
  },
  confirmDialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      overflow: 'hidden'
    }
  },
  confirmDialogTitle: {
    background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
    color: 'white',
    padding: theme.spacing(2),
    '& .MuiTypography-h6': {
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    }
  },
  confirmDeleteButton: {
    background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
    color: 'white',
    fontWeight: 600,
    borderRadius: 25,
    padding: theme.spacing(1, 3),
    boxShadow: '0 4px 15px rgba(244, 67, 54, 0.4)',
    '&:hover': {
      background: 'linear-gradient(45deg, #ee5a24, #ff6b6b)',
      boxShadow: '0 6px 20px rgba(244, 67, 54, 0.6)',
      transform: 'translateY(-1px)'
    }
  },
  tabBadge: {
    '& .MuiBadge-badge': {
      right: -8,
      top: 8,
      fontSize: '0.7rem',
      height: 18,
      minWidth: 18,
      padding: '0 4px',
      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
      color: 'white',
      fontWeight: 600,
      transform: 'scale(0.9)',
      boxShadow: `0 2px 8px ${theme.palette.primary.main}40`
    }
  },
  // Estilos para o modal de visualização de mídia
  mediaViewerDialog: {
    '& .MuiDialog-paper': {
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      maxWidth: '90vw',
      maxHeight: '90vh',
      borderRadius: 8,
    }
  },
  mediaViewerContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    backgroundColor: 'black',
    position: 'relative'
  },
  mediaElement: {
    maxWidth: '100%',
    maxHeight: '70vh',
    objectFit: 'contain'
  },
  audioContainer: {
    padding: theme.spacing(5),
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3)
  },
  requiredInfoBox: {
    background: theme.palette.mode === 'light'
      ? 'linear-gradient(135deg, rgba(33,150,243,0.10) 0%, rgba(156,39,176,0.10) 100%)'
      : 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 100%)',
    border: theme.palette.mode === 'light'
      ? `1px solid ${theme.palette.primary.light}55`
      : '1px solid rgba(255,255,255,0.12)',
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    marginBottom: theme.spacing(3)
  },
  requiredInfoTitle: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    '& strong': { fontWeight: 600 }
  },
  requiredInfoHint: {
    color: theme.palette.text.secondary,
    marginTop: 4,
    display: 'block'
  },
  requiredInfoStatus: {
    marginTop: 8,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    '&.success': { color: theme.palette.success.main },
    '&.warning': { color: theme.palette.warning.main }
  },
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`task-tabpanel-${index}`}
      aria-labelledby={`task-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box className={other.className}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TaskFormModal = ({ open, onClose, task, onSave }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);

  // Estados do formulário principal
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: '',
    dueDate: '',
    dueTime: '', // Novo campo para hora
    isPublic: false, // Adicionar campo para visibilidade
    // Recorrência
    isRecurring: false,
    recurrenceType: 'daily',
    recurrenceInterval: 1,
    recurrenceEndDate: '',
    recurrenceCount: '',
    // Notificações WhatsApp
    notificationsEnabled: true, // Por padrão habilitado
    // Lembretes personalizados
    reminderEnabled: false,
    reminderDate: '',
    reminderTime: ''
  });

  // Estados para funcionalidades extras
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtask, setNewSubtask] = useState({
    title: '',
    priority: 'medium',
    dueDate: ''
  });
  const [editingSubtask, setEditingSubtask] = useState(null); // Para controlar qual subtarefa está sendo editada

  const [attachments, setAttachments] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState(null); // Para controlar qual comentário está sendo editado

  // Estados para controle de confirmações de exclusão
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState('');
  
  // Listas para controlar exclusões pendentes
  const [pendingSubtaskDeletes, setPendingSubtaskDeletes] = useState([]);
  const [pendingCommentDeletes, setPendingCommentDeletes] = useState([]);
  const [pendingAttachmentDeletes, setPendingAttachmentDeletes] = useState([]);

  // Estados para o modal de visualização de mídia
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [currentMedia, setCurrentMedia] = useState(null);
  const [mediaLoading, setMediaLoading] = useState(false);
  const { users: allUsers } = useUsers();
  const [assignees, setAssignees] = useState([]);

  // Estado para controlar erros de validação
  const [validationErrors, setValidationErrors] = useState({});

  // Carregar dados se estiver editando
  useEffect(() => {
    if (task && open) {
      console.log('[TASK FORM MODAL] Recebendo task:', task);
      console.log('[TASK FORM MODAL] Anexos recebidos:', task.attachments);
      
      // Função auxiliar para formatar data com validação
      const formatDateSafely = (dateValue) => {
        if (!dateValue) return '';
        try {
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) return '';
          return format(date, 'yyyy-MM-dd');
        } catch (error) {
          console.warn('Data inválida:', dateValue, error);
          return '';
        }
      };

      // Função auxiliar para extrair hora da data
      const formatTimeSafely = (dateValue) => {
        if (!dateValue) return '';
        try {
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) return '';
          return format(date, 'HH:mm');
        } catch (error) {
          console.warn('Hora inválida:', dateValue, error);
          return '';
        }
      };

      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        category: task.category || '',
        dueDate: formatDateSafely(task.dueDate),
        dueTime: formatTimeSafely(task.dueDate), // Extrair hora da data
        isPublic: task.isPublic || false, // Adicionar campo isPublic
        isRecurring: task.isRecurring || false,
        recurrenceType: task.recurrenceType || 'daily',
        recurrenceInterval: task.recurrenceInterval || 1,
        recurrenceEndDate: formatDateSafely(task.recurrenceEndDate),
        recurrenceCount: task.recurrenceCount || '',
        // Notificações WhatsApp - padrão true se não definido
        notificationsEnabled: task.notificationsEnabled !== undefined ? task.notificationsEnabled : true,
        // Lembretes personalizados
        reminderEnabled: task.reminderEnabled || false,
        reminderDate: formatDateSafely(task.reminderDate),
        reminderTime: formatTimeSafely(task.reminderDate)
      });

      // Carregar responsáveis (assignees)
      const loadAssignees = async () => {
        if (task.assignees && Array.isArray(task.assignees)) {
          setAssignees(task.assignees.map(a => a.user).filter(Boolean));
          return;
        }
        if (task.id) {
          try {
            const current = await taskService.getAssignees(task.id);
            setAssignees(current.map(a => a.user).filter(Boolean));
          } catch (e) { /* silencioso */ }
        }
      };
      loadAssignees();
      
      // Se a task já tem os dados carregados (anexos, subtarefas, comentários), usar eles
      if (task.attachments || task.subtasks || task.comments) {
        console.log('[TASK FORM MODAL] Usando dados já carregados');
        
        // Carregar subtarefas se existirem
  if (task.subtasks && Array.isArray(task.subtasks)) {
          console.log('[TASK FORM MODAL] Processando subtarefas:', task.subtasks);
          console.log('[TASK FORM MODAL] Exemplo de subtarefa com dados do usuário:', task.subtasks[0]);
          setSubtasks(task.subtasks.map(subtask => ({
            id: subtask.id,
            title: subtask.title,
            priority: subtask.priority || 'medium',
            dueDate: formatDateSafely(subtask.dueDate),
            status: subtask.status || 'pending',
            completedAt: subtask.completedAt || null,
            user: subtask.user || null, // Incluir dados do usuário
            createdAt: subtask.createdAt,
            updatedAt: subtask.updatedAt
          })));
        }
        
        // Carregar comentários se existirem
        if (task.comments && Array.isArray(task.comments)) {
          console.log('[TASK COMMENTS] Comentários da tarefa:', task.comments);
          setComments(task.comments.map(comment => {
            console.log('[TASK COMMENTS] Processando comentário:', comment);
            console.log('[TASK COMMENTS] Dados do usuário:', comment.user);
            return {
              id: comment.id,
              content: comment.content,
              createdAt: comment.createdAt,
              user: comment.user || { 
                id: null,
                name: 'Usuário Desconhecido',
                email: null
              }
            };
          }));
        }
        
        // Carregar anexos se existirem
        if (task.attachments && Array.isArray(task.attachments)) {
          console.log('[TASK FORM MODAL] Processando anexos:', task.attachments);
          console.log('[TASK FORM MODAL] Exemplo de anexo com dados do usuário:', task.attachments[0]);
          setAttachments(task.attachments.map(attachment => ({
            id: attachment.id,
            originalName: attachment.originalName,
            size: attachment.size,
            mimetype: attachment.mimetype,
            url: attachment.url,
            createdAt: attachment.createdAt,
            user: attachment.user || null, // Incluir dados do usuário
            // Anexos do servidor não são novos (explicitamente false)
            isNew: false
          })));
          console.log('[TASK FORM MODAL] Anexos definidos no state');
        }
      } else {
        // Se não tem dados carregados, carregar via API (modo antigo)
        console.log('[TASK FORM MODAL] Carregando dados via API');
        loadTaskExtras(task.id);
      }
    } else {
      resetForm();
    }
  }, [task, open]);

  // Função para carregar dados extras da tarefa
  const loadTaskExtras = async (taskId) => {
    try {
      setLoading(true);
      
      // Carregar subtarefas, comentários e anexos em paralelo
      const [subtasksRes, commentsRes, attachmentsRes] = await Promise.all([
        taskService.getSubtasks(taskId).catch(() => ({ subtasks: [] })),
        taskService.getComments(taskId).catch(() => ({ comments: [] })),
        taskService.getAttachments(taskId).catch(() => [])
      ]);
      
      // Definir subtarefas
  if (subtasksRes.subtasks) {
        console.log('[TASK FORM MODAL] Carregando subtarefas via API:', subtasksRes.subtasks);
        console.log('[TASK FORM MODAL] Exemplo de subtarefa com dados do usuário:', subtasksRes.subtasks[0]);
        setSubtasks(subtasksRes.subtasks.map(subtask => {
          // Função auxiliar para formatar data com validação
          const formatDateSafely = (dateValue) => {
            if (!dateValue) return '';
            try {
              const date = new Date(dateValue);
              if (isNaN(date.getTime())) return '';
              return format(date, 'yyyy-MM-dd');
            } catch (error) {
              console.warn('Data inválida na subtarefa:', dateValue, error);
              return '';
            }
          };

          return {
            id: subtask.id,
            title: subtask.title,
            priority: subtask.priority || 'medium',
            dueDate: formatDateSafely(subtask.dueDate),
            status: subtask.status || 'pending',
            completedAt: subtask.completedAt || null,
            user: subtask.user || null, // Incluir dados do usuário
            createdAt: subtask.createdAt,
            updatedAt: subtask.updatedAt
          };
        }));
      }
      
      // Definir comentários
      if (commentsRes.comments) {
        console.log('[LOAD COMMENTS] Comentários recebidos da API:', commentsRes.comments);
        setComments(commentsRes.comments.map(comment => {
          console.log('[LOAD COMMENTS] Processando comentário:', comment);
          console.log('[LOAD COMMENTS] Dados do usuário do comentário:', comment.user);
          return {
            id: comment.id,
            content: comment.content,
            createdAt: comment.createdAt,
            user: comment.user || { 
              id: null,
              name: 'Usuário Desconhecido',
              email: null
            }
          };
        }));
      }
      
      // Definir anexos
      if (attachmentsRes && attachmentsRes.attachments && Array.isArray(attachmentsRes.attachments)) {
        console.log('[TASK FORM MODAL] Carregando anexos via API:', attachmentsRes.attachments);
        console.log('[TASK FORM MODAL] Exemplo de anexo com dados do usuário:', attachmentsRes.attachments[0]);
        setAttachments(attachmentsRes.attachments.map(attachment => ({
          id: attachment.id,
          originalName: attachment.originalName,
          size: attachment.size,
          mimetype: attachment.mimetype,
          url: attachment.url,
          createdAt: attachment.createdAt,
          user: attachment.user || null, // Incluir dados do usuário
          // Anexos do servidor não são novos (explicitamente false)
          isNew: false
        })));
      }
      
    } catch (error) {
      console.error('Erro ao carregar dados extras da tarefa:', error);
      toast.error('Erro ao carregar dados completos da tarefa');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      category: '',
      dueDate: '',
      dueTime: '', // Resetar campo de hora
      isPublic: false, // Adicionar campo isPublic no reset
      isRecurring: false,
      recurrenceType: 'daily',
      recurrenceInterval: 1,
      recurrenceEndDate: '',
      recurrenceCount: '',
      notificationsEnabled: true, // Resetar para padrão
    });
    setSubtasks([]);
    setNewSubtask({ title: '', priority: 'medium', dueDate: '' });
    setEditingSubtask(null); // Resetar estado de edição
    setAttachments([]);
    setComments([]);
    setNewComment('');
    setEditingComment(null); // Resetar estado de edição de comentário
    setTabValue(0);
    
    // Resetar estados de confirmação e exclusões pendentes
    setConfirmDeleteOpen(false);
    setDeleteAction(null);
    setDeleteMessage('');
    setPendingSubtaskDeletes([]);
    setPendingCommentDeletes([]);
    setPendingAttachmentDeletes([]);
  setAssignees([]);
    
    // Limpar erros de validação
    setValidationErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro de validação quando o usuário começar a digitar
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Funções auxiliares para confirmação de exclusão
  const openDeleteConfirmation = (message, action) => {
    setDeleteMessage(message);
    setDeleteAction(() => action);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteAction) {
      deleteAction();
    }
    setConfirmDeleteOpen(false);
    setDeleteAction(null);
    setDeleteMessage('');
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setDeleteAction(null);
    setDeleteMessage('');
  };

  const handleAddSubtask = () => {
    if (!newSubtask.title.trim()) {
      toast.warning('Título da subtarefa é obrigatório');
      return;
    }
    
    const subtask = {
      id: `temp_${Date.now()}`, // ID temporário claramente identificável
      title: newSubtask.title,
      priority: newSubtask.priority,
      dueDate: newSubtask.dueDate,
      status: 'pending',
      user: user ? { // Incluir usuário atual para subtarefas novas
        id: user.id,
        name: user.name,
        email: user.email
      } : null,
      createdAt: new Date().toISOString(),
      isNew: true // Flag para identificar como novo
    };
    
    setSubtasks(prev => [...prev, subtask]);
    setNewSubtask({ title: '', priority: 'medium', dueDate: '' });
    toast.success('Subtarefa adicionada!');
  };

  const handleRemoveSubtask = (index) => {
    const subtaskToRemove = subtasks[index];
    console.log('[REMOVE SUBTASK] Subtarefa a remover:', subtaskToRemove);
    
    // Se a subtarefa já existe no banco (tem ID numérico e não é nova)
    if (subtaskToRemove.id && !subtaskToRemove.isNew && typeof subtaskToRemove.id === 'number') {
      console.log('[REMOVE SUBTASK] É uma subtarefa existente no banco, marcando para exclusão pendente');
      openDeleteConfirmation(
        `Tem certeza que deseja excluir a subtarefa "${subtaskToRemove.title}"?`,
        () => {
          // Apenas marcar para exclusão pendente, NÃO remover da interface
          setPendingSubtaskDeletes(prev => {
            const updated = [...prev, subtaskToRemove.id];
            console.log('[REMOVE SUBTASK] Lista de exclusões pendentes atualizada:', updated);
            return updated;
          });
          toast.info('Subtarefa marcada para exclusão. Será removida ao salvar a tarefa.');
        }
      );
    } else {
      console.log('[REMOVE SUBTASK] É uma subtarefa nova, removendo diretamente da interface');
      // Se é uma subtarefa nova (apenas na interface), remove diretamente
      openDeleteConfirmation(
        `Tem certeza que deseja remover a subtarefa "${subtaskToRemove.title}"?`,
        () => {
          setSubtasks(prev => prev.filter((_, i) => i !== index));
          toast.success('Subtarefa removida!');
        }
      );
    }
  };

  const handleEditSubtask = (index) => {
    setEditingSubtask(index);
  };

  const handleCancelEditSubtask = () => {
    setEditingSubtask(null);
  };

  const handleSaveEditSubtask = async (index, updatedSubtask) => {
    if (!updatedSubtask.title.trim()) {
      toast.warning('Título da subtarefa é obrigatório');
      return;
    }

    const currentSubtask = subtasks[index];
    
    // Se a subtarefa já existe no banco, fazer chamada para API
    if (currentSubtask.id && !currentSubtask.isNew && typeof currentSubtask.id === 'number') {
      try {
        await taskService.updateSubtask(currentSubtask.id, updatedSubtask);
        setSubtasks(prev => prev.map((subtask, i) => 
          i === index ? { ...subtask, ...updatedSubtask } : subtask
        ));
        toast.success('Subtarefa atualizada com sucesso!');
      } catch (error) {
        console.error('Erro ao atualizar subtarefa:', error);
        toast.error('Erro ao atualizar subtarefa');
        return;
      }
    } else {
      // Se é uma subtarefa nova (apenas na interface), atualizar diretamente
      setSubtasks(prev => prev.map((subtask, i) => 
        i === index ? { ...subtask, ...updatedSubtask } : subtask
      ));
      toast.success('Subtarefa atualizada!');
    }
    
    setEditingSubtask(null);
  };

  const handleToggleSubtaskStatus = async (index) => {
    const current = subtasks[index];
    if (!current) return;
    if (pendingSubtaskDeletes.includes(current.id)) return;

    const nextStatus = current.status === 'completed' ? 'pending' : 'completed';

    // Otimista no state
    const optimistic = {
      ...current,
      status: nextStatus,
      // se completar agora, define completedAt localmente; ao reabrir, zera
      completedAt: nextStatus === 'completed' ? new Date().toISOString() : null,
      updatedAt: new Date().toISOString()
    };
    setSubtasks(prev => prev.map((s, i) => i === index ? optimistic : s));

    // Persistir se já existe no banco (id numérico e não isNew)
    if (current.id && !current.isNew && typeof current.id === 'number') {
      try {
        await taskService.updateSubtask(current.id, { status: nextStatus });
        toast.success(nextStatus === 'completed' ? 'Subtarefa concluída!' : 'Subtarefa reaberta!');
      } catch (error) {
        // rollback em caso de erro
        setSubtasks(prev => prev.map((s, i) => i === index ? current : s));
        console.error('Erro ao alternar status da subtarefa:', error);
        toast.error('Erro ao alternar status da subtarefa');
      }
    } else {
      // Apenas local para subtarefas novas
      toast.success(nextStatus === 'completed' ? 'Subtarefa marcada como concluída' : 'Subtarefa marcada como pendente');
    }
  };

  const handleSubtaskFieldChange = (index, field, value) => {
    setSubtasks(prev => prev.map((subtask, i) => 
      i === index ? { ...subtask, [field]: value } : subtask
    ));
  };

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    
    console.log('[ADD COMMENT] Dados do usuário atual:', user);
    
    const comment = {
      id: `temp_${Date.now()}`,
      content: newComment,
      createdAt: new Date().toISOString(),
      user: { 
        id: user?.id || null,
        name: user?.name || 'Usuário Desconhecido',
        email: user?.email || null
      },
      isNew: true // Flag para identificar como novo
    };
    
    console.log('[ADD COMMENT] Novo comentário criado:', comment);
    setComments(prev => [...prev, comment]);
    setNewComment('');
    toast.success('Comentário adicionado!');
  };

  const handleEditComment = (index) => {
    setEditingComment(index);
  };

  const handleCancelEditComment = () => {
    setEditingComment(null);
  };

  const handleSaveEditComment = async (index) => {
    const currentComment = comments[index];
    const updatedContent = currentComment.content;
    
    if (!updatedContent.trim()) {
      toast.warning('Conteúdo do comentário é obrigatório');
      return;
    }

    // Se o comentário já existe no banco, fazer chamada para API
    if (currentComment.id && !currentComment.isNew && typeof currentComment.id === 'number') {
      try {
        console.log('[EDIT COMMENT] Tentando atualizar comentário:', currentComment.id, 'com conteúdo:', updatedContent);
        // Enviar apenas a string do conteúdo, não um objeto
        await taskService.updateComment(currentComment.id, updatedContent);
        toast.success('Comentário atualizado com sucesso!');
      } catch (error) {
        console.error('Erro ao atualizar comentário:', error);
        
        // Se o comentário não foi encontrado (404), remover da interface
        if (error.response?.status === 404 || error.message?.includes('não encontrado')) {
          toast.warning('Comentário não encontrado. Removendo da lista...');
          setComments(prev => prev.filter((_, i) => i !== index));
          setEditingComment(null);
          return;
        }
        
        toast.error('Erro ao atualizar comentário');
        return;
      }
    } else {
      // Se é um comentário novo (apenas na interface), atualizar diretamente
      console.log('[EDIT COMMENT] Atualizando comentário novo localmente');
      toast.success('Comentário atualizado!');
    }
    
    setEditingComment(null);
  };

  const handleRemoveComment = (index) => {
    const commentToRemove = comments[index];
    console.log('[REMOVE COMMENT] Comentário a remover:', commentToRemove);
    
    // Se o comentário já existe no banco (tem ID numérico e não é novo)
    if (commentToRemove.id && !commentToRemove.isNew && typeof commentToRemove.id === 'number') {
      console.log('[REMOVE COMMENT] É um comentário existente no banco, marcando para exclusão pendente');
      openDeleteConfirmation(
        'Tem certeza que deseja excluir este comentário?',
        () => {
          // Apenas marcar para exclusão pendente, NÃO remover da interface
          setPendingCommentDeletes(prev => {
            const updated = [...prev, commentToRemove.id];
            console.log('[REMOVE COMMENT] Lista de exclusões pendentes atualizada:', updated);
            return updated;
          });
          toast.info('Comentário marcado para exclusão. Será removido ao salvar a tarefa.');
        }
      );
    } else {
      console.log('[REMOVE COMMENT] É um comentário novo, removendo diretamente da interface');
      // Se é um comentário novo (apenas na interface), remove diretamente
      openDeleteConfirmation(
        'Tem certeza que deseja remover este comentário?',
        () => {
          setComments(prev => prev.filter((_, i) => i !== index));
          toast.success('Comentário removido!');
        }
      );
    }
  };

  const handleCommentFieldChange = (index, value) => {
    setComments(prev => prev.map((comment, i) => 
      i === index ? { ...comment, content: value } : comment
    ));
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    
    files.forEach(file => {
      const attachment = {
        id: `temp_${Date.now()}_${Math.random()}`, // ID string para anexos novos
        originalName: file.name,
        size: file.size,
        file: file, // Arquivo real para upload posterior
        createdAt: new Date().toISOString(),
        user: user ? { // Incluir usuário atual para anexos novos
          id: user.id,
          name: user.name,
          email: user.email
        } : null,
        isNew: true // Marcar explicitamente como novo
      };
      
      setAttachments(prev => [...prev, attachment]);
    });
    
    toast.success(`${files.length} arquivo(s) anexado(s)!`);
    event.target.value = ''; // Limpar input
  };

  const handleRemoveAttachment = (id) => {
    const attachmentToRemove = attachments.find(att => att.id === id);
    console.log('[REMOVE ATTACHMENT] Anexo a remover:', attachmentToRemove);
    
    if (!attachmentToRemove) {
      toast.warning('Anexo não encontrado');
      return;
    }
    
    // Se o anexo já existe no banco (tem ID numérico e não foi marcado como novo)
    // Anexos vindos do servidor têm IDs numéricos e não têm isNew=true
    const isExistingAttachment = typeof attachmentToRemove.id === 'number' && attachmentToRemove.isNew !== true;
    console.log('[REMOVE ATTACHMENT] É anexo existente?', isExistingAttachment);
    
    if (isExistingAttachment) {
      console.log('[REMOVE ATTACHMENT] É um anexo existente no banco, marcando para exclusão pendente');
      openDeleteConfirmation(
        `Tem certeza que deseja excluir o anexo "${attachmentToRemove.originalName}"?`,
        () => {
          // Apenas marcar para exclusão pendente, NÃO remover da interface
          setPendingAttachmentDeletes(prev => {
            const updated = [...prev, attachmentToRemove.id];
            console.log('[REMOVE ATTACHMENT] Lista de exclusões pendentes atualizada:', updated);
            return updated;
          });
          toast.info('Anexo marcado para exclusão. Será removido ao salvar a tarefa.');
        }
      );
    } else {
      console.log('[REMOVE ATTACHMENT] É um anexo novo, removendo diretamente da interface');
      // Se é um anexo novo (apenas na interface), remove diretamente
      openDeleteConfirmation(
        `Tem certeza que deseja remover o anexo "${attachmentToRemove.originalName}"?`,
        () => {
          setAttachments(prev => prev.filter(att => att.id !== id));
          toast.success('Anexo removido!');
        }
      );
    }
  };

  const getFileIcon = (filename, mimetype) => {
    const extension = filename?.toLowerCase().split('.').pop();
    const type = mimetype?.toLowerCase();
    
    // Cores dinâmicas baseadas no tema
    const imageColor = theme.palette.info.main;
    const videoColor = theme.palette.error.main;
    const audioColor = theme.palette.secondary.main;
    const documentColor = theme.palette.primary.main;
    const archiveColor = theme.palette.warning.main;
    const defaultColor = theme.palette.text.secondary;
    
    if (type?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
      return <ImageIcon className={classes.fileIcon} style={{ color: imageColor }} />;
    }
    if (type?.includes('video') || ['mp4', 'avi', 'mkv', 'mov', 'wmv', 'flv'].includes(extension)) {
      return <VideoIcon className={classes.fileIcon} style={{ color: videoColor }} />;
    }
    if (type?.includes('audio') || ['mp3', 'wav', 'ogg', 'flac', 'm4a'].includes(extension)) {
      return <AudioIcon className={classes.fileIcon} style={{ color: audioColor }} />;
    }
    if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(extension)) {
      return <DocumentIcon className={classes.fileIcon} style={{ color: documentColor }} />;
    }
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
      return <AttachIcon className={classes.fileIcon} style={{ color: archiveColor }} />;
    }
    return <AttachIcon className={classes.fileIcon} style={{ color: defaultColor }} />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Função para verificar se o arquivo é uma mídia visualizável
  const isViewableMedia = (filename, mimetype) => {
    if (!filename && !mimetype) return false;
    
    const extension = filename ? filename.split('.').pop().toLowerCase() : '';
    const type = mimetype ? mimetype.toLowerCase() : '';
    
    // Imagens
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const imageTypes = ['image/'];
    
    // Vídeos
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'];
    const videoTypes = ['video/'];
    
    // Áudios
    const audioExtensions = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'];
    const audioTypes = ['audio/'];
    
    // Verificar por extensão
    if (imageExtensions.includes(extension) || 
        videoExtensions.includes(extension) || 
        audioExtensions.includes(extension)) {
      return true;
    }
    
    // Verificar por mimetype
    if (imageTypes.some(t => type.startsWith(t)) ||
        videoTypes.some(t => type.startsWith(t)) ||
        audioTypes.some(t => type.startsWith(t))) {
      return true;
    }
    
    return false;
  };

  // Função para obter o tipo de mídia
  const getMediaType = (filename, mimetype) => {
    if (!filename && !mimetype) return 'unknown';
    
    const extension = filename ? filename.split('.').pop().toLowerCase() : '';
    const type = mimetype ? mimetype.toLowerCase() : '';
    
    // Verificar imagens
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    if (imageExtensions.includes(extension) || type.startsWith('image/')) {
      return 'image';
    }
    
    // Verificar vídeos
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'];
    if (videoExtensions.includes(extension) || type.startsWith('video/')) {
      return 'video';
    }
    
    // Verificar áudios
    const audioExtensions = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'];
    if (audioExtensions.includes(extension) || type.startsWith('audio/')) {
      return 'audio';
    }
    
    return 'document';
  };

  const handleViewAttachment = (attachment) => {
    try {
      // Verificar se é mídia visualizável
      if (!isViewableMedia(attachment.originalName, attachment.mimetype)) {
        toast.info('Este tipo de arquivo não pode ser visualizado. Use o botão de download.');
        return;
      }
      
      // Estratégia de múltiplas URLs para tentar carregar o arquivo
      let possibleUrls = [];
      
      // 1. URL baseada no path do arquivo estático (mais provável de funcionar)
      if (attachment.url) {
        const baseURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
        // Limpar o path removendo redundâncias e navegação de diretórios
        let cleanPath = attachment.url
          .replace(/^\/public\//, '') // Removes /public/ do início
          .replace(/\/\.\.\//g, '/') // Removes /../
          .replace(/\/\/+/g, '/') // Removes barras duplas
          .replace(/^\//, ''); // Removes barra inicial se houver
        
        possibleUrls.push(`${baseURL}/public/${cleanPath}`);
        console.log('[ATTACHMENT VIEW] URL limpa gerada:', `${baseURL}/public/${cleanPath}`);
        console.log('[ATTACHMENT VIEW] Path original:', attachment.url);
        console.log('[ATTACHMENT VIEW] Path limpo:', cleanPath);
      }
      
      // 2. URL direta original como fallback
      if (attachment.url) {
        possibleUrls.push(attachment.url);
      }
      
      // 3. URLs de API específicas se disponíveis
      if (attachment.id) {
        possibleUrls.push(taskService.getAttachmentViewUrl(attachment.id));
        possibleUrls.push(taskService.getAttachmentDownloadUrl(attachment.id));
      }
      
      // 4. URLs alternativas baseadas no padrão comum de APIs
      if (attachment.id) {
        const baseURL = process.env.REACT_APP_BACKEND_URL || '';
        possibleUrls.push(`${baseURL}/attachments/${attachment.id}`);
        possibleUrls.push(`${baseURL}/files/${attachment.id}`);
        possibleUrls.push(`${baseURL}/uploads/${attachment.originalName}`);
      }
      
      console.log('[ATTACHMENT VIEW] URLs possíveis para testar:', possibleUrls);
      console.log('[ATTACHMENT VIEW] Dados do anexo:', attachment);
      console.log('[ATTACHMENT VIEW] Mimetype:', attachment.mimetype);
      console.log('[ATTACHMENT VIEW] Tipo detectado:', getMediaType(attachment.originalName, attachment.mimetype));
      
      if (possibleUrls.length === 0) {
        toast.warning('Não foi possível obter a URL do arquivo');
        return;
      }
      
      // Configurar mídia para o modal com a primeira URL
      setCurrentMedia({
        url: possibleUrls[0],
        name: attachment.originalName,
        type: getMediaType(attachment.originalName, attachment.mimetype),
        mimetype: attachment.mimetype,
        fallbackUrls: possibleUrls.slice(1) // URLs alternativas para fallback
      });
      
      // Testar se a primeira URL funciona
      console.log('[ATTACHMENT VIEW] Testando primeira URL:', possibleUrls[0]);
      const testImage = new Image();
      testImage.onload = () => {
        console.log('[ATTACHMENT VIEW] ✅ Primeira URL funcionou!');
      };
      testImage.onerror = () => {
        console.log('[ATTACHMENT VIEW] ❌ Primeira URL falhou, fallback será usado automaticamente');
      };
      testImage.src = possibleUrls[0];
      
      setMediaViewerOpen(true);
      setMediaLoading(true); // Iniciar estado de carregamento
      console.log('[ATTACHMENT VIEW] Abrindo modal para mídia:', attachment.originalName);
      
    } catch (error) {
      console.error('Erro ao visualizar anexo:', error);
      toast.error('Erro ao abrir arquivo para visualização');
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      console.log('[ATTACHMENT DOWNLOAD] Iniciando download:', attachment);
      
      // Usar o serviço dedicado de download
      if (attachment.id) {
        await taskService.downloadAttachment(attachment.id, attachment.originalName);
        toast.success(`Download de "${attachment.originalName}" iniciado!`);
        return;
      }
      
      // Fallback para URL direta se disponível
      if (attachment.url) {
        const link = document.createElement('a');
        link.href = attachment.url;
        link.download = attachment.originalName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Download de "${attachment.originalName}" iniciado!`);
        return;
      }
      
      toast.warning('Download não disponível para este arquivo');
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast.error(`Erro ao fazer download: ${error.message || 'Erro desconhecido'}`);
    }
  };

  const handleSave = async () => {
    // Validação de campos obrigatórios
    const validationErrorsList = [];
    const newValidationErrors = {};
    
    if (!formData.title.trim()) {
      validationErrorsList.push('Título da tarefa');
      newValidationErrors.title = true;
    }
    
    if (!formData.dueDate) {
      validationErrorsList.push('Data de vencimento');
      newValidationErrors.dueDate = true;
    }
    
    // Se há erros de validação, mostrar mensagem e parar
    if (validationErrorsList.length > 0) {
      setValidationErrors(newValidationErrors);
      const errorMessage = `⚠️ Os seguintes campos são obrigatórios: ${validationErrorsList.join(', ')}`;
      toast.error(errorMessage);
      console.warn('[SAVE] Validação falhou:', { 
        errors: validationErrorsList, 
        formData: { 
          title: formData.title, 
          category: formData.category, 
          dueDate: formData.dueDate 
        } 
      });
      
      // Focar no primeiro campo com erro
      setTimeout(() => {
        const firstErrorField = document.querySelector('.Mui-error input, .Mui-error textarea');
        if (firstErrorField) {
          firstErrorField.focus();
        }
      }, 100);
      
      return;
    }
    
    // Limpar erros de validação se passou na validação
    setValidationErrors({});

    try {
      setLoading(true);
      
      let totalDeleted = 0;
      
      // Processar exclusões pendentes antes de salvar
      if (pendingSubtaskDeletes.length > 0) {
        console.log('[SAVE] Processando exclusões de subtarefas:', pendingSubtaskDeletes);
        for (const subtaskId of pendingSubtaskDeletes) {
          try {
            await taskService.deleteSubtask(subtaskId);
            console.log('[SAVE] Subtarefa excluída:', subtaskId);
            totalDeleted++;
          } catch (error) {
            console.error('Erro ao excluir subtarefa:', error);
            if (error.response?.status !== 404) {
              toast.warning(`Erro ao excluir uma subtarefa: ${error.message}`);
            }
          }
        }
      }

      if (pendingCommentDeletes.length > 0) {
        console.log('[SAVE] Processando exclusões de comentários:', pendingCommentDeletes);
        for (const commentId of pendingCommentDeletes) {
          try {
            await taskService.deleteComment(commentId);
            console.log('[SAVE] Comentário excluído:', commentId);
            totalDeleted++;
          } catch (error) {
            console.error('Erro ao excluir comentário:', error);
            if (error.response?.status !== 404) {
              toast.warning(`Erro ao excluir um comentário: ${error.message}`);
            }
          }
        }
      }

      if (pendingAttachmentDeletes.length > 0) {
        console.log('[SAVE] Processando exclusões de anexos:', pendingAttachmentDeletes);
        for (const attachmentId of pendingAttachmentDeletes) {
          try {
            await taskService.deleteAttachment(attachmentId);
            console.log('[SAVE] Anexo excluído:', attachmentId);
            totalDeleted++;
          } catch (error) {
            console.error('Erro ao excluir anexo:', error);
            if (error.response?.status !== 404) {
              toast.warning(`Erro ao excluir um anexo: ${error.message}`);
            }
          }
        }
      }
      
      // Filtrar os itens que foram marcados para exclusão antes de enviar para o servidor
      const filteredSubtasks = subtasks.filter(subtask => !pendingSubtaskDeletes.includes(subtask.id));
      const filteredComments = comments.filter(comment => !pendingCommentDeletes.includes(comment.id));
      const filteredAttachments = attachments.filter(attachment => !pendingAttachmentDeletes.includes(attachment.id));
      
      // Função para combinar data e hora
      const combineDateAndTime = (dateStr, timeStr) => {
        if (!dateStr) return null;
        
        // Se não há hora especificada, usar 23:59 para o final do dia
        const time = timeStr || '23:59';
        const dateTimeStr = `${dateStr}T${time}:00`;
        
        try {
          const combinedDate = new Date(dateTimeStr);
          if (isNaN(combinedDate.getTime())) {
            console.warn('Data/hora inválida:', dateTimeStr);
            return dateStr; // Fallback para apenas a data
          }
          return combinedDate.toISOString();
        } catch (error) {
          console.warn('Erro ao combinar data e hora:', error);
          return dateStr; // Fallback para apenas a data
        }
      };
      
      const taskData = {
        ...formData,
        dueDate: combineDateAndTime(formData.dueDate, formData.dueTime), // Combinar data e hora
        reminderEnabled: formData.reminderEnabled, // Garantir que reminderEnabled seja incluído
        notificationsEnabled: formData.notificationsEnabled, // Garantir que notificationsEnabled seja incluído
        reminderDate: formData.reminderEnabled && formData.reminderDate && formData.reminderTime 
          ? combineDateAndTime(formData.reminderDate, formData.reminderTime) 
          : null, // Combinar data e hora do lembrete
        subtasks: filteredSubtasks,
        comments: filteredComments,
        attachments: filteredAttachments,
        // Enviar responsáveis na criação (backend já suporta em CreateTaskService)
        // Apenas quando não for pública, pois responsáveis só fazem sentido para tarefas privadas
        ...(formData.isPublic ? {} : { assignees: (assignees || []).map(u => u.id) })
      };

      // Remover os campos temporários dos dados a serem enviados
      delete taskData.dueTime;
      delete taskData.reminderTime;

      // Debug: Verificar se campos essenciais estão sendo incluídos
      console.log('[TASK_FORM] 🔍 Dados sendo enviados:', {
        id: taskData.id,
        title: taskData.title,
        reminderEnabled: taskData.reminderEnabled,
        notificationsEnabled: taskData.notificationsEnabled,
        reminderDate: taskData.reminderDate,
        // RECORRÊNCIA - Debug
        isRecurring: taskData.isRecurring,
        recurrenceType: taskData.recurrenceType,
        recurrenceInterval: taskData.recurrenceInterval,
        recurrenceEndDate: taskData.recurrenceEndDate,
        recurrenceCount: taskData.recurrenceCount
      });

      await onSave(taskData);

      // Após salvar principal, sincronizar responsáveis quando não for pública
      if (!formData.isPublic) {
        try {
          // Buscar atuais no backend para comparar
          const currentAssignees = task && task.id ? await taskService.getAssignees(task.id) : [];
          const currentIds = new Set((currentAssignees || []).map(a => a.userId));
          const selectedIds = new Set((assignees || []).map(u => u.id));

          // Adicionar novos
          if (task && task.id) {
            for (const uid of selectedIds) {
              if (!currentIds.has(uid)) {
                await taskService.addAssignee(task.id, uid);
              }
            }
            // Remover os que saíram
            for (const uid of currentIds) {
              if (!selectedIds.has(uid)) {
                await taskService.removeAssignee(task.id, uid);
              }
            }
          }
        } catch (e) {
          console.error('Erro ao sincronizar responsáveis:', e);
        }
      }
      
      // Disparar evento personalizado para atualizar notificações
      const eventType = task ? 'taskUpdated' : 'taskCreated';
      console.log(`[TASK_FORM] Disparando evento ${eventType} para atualizar notificações`, {
        taskId: task?.id,
        reminderEnabled: taskData.reminderEnabled,
        reminderDate: taskData.reminderDate
      });
      
      window.dispatchEvent(new CustomEvent(eventType, { 
        detail: { taskId: task?.id, taskData } 
      }));
      
      // Atualizar estados locais removendo os itens excluídos
      setSubtasks(filteredSubtasks);
      setComments(filteredComments);
      setAttachments(filteredAttachments);
      
      // Limpar listas de exclusões pendentes
      setPendingSubtaskDeletes([]);
      setPendingCommentDeletes([]);
      setPendingAttachmentDeletes([]);
      
      // Mostrar mensagem de sucesso sobre exclusões se houveram
      if (totalDeleted > 0) {
        toast.success(`Tarefa salva e ${totalDeleted} item(ns) excluído(s) com sucesso!`);
      }
      
      onClose();
    } catch (error) {
      console.error('Erro ao salvar tarefa:', error);
      toast.error('Erro ao salvar tarefa');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'default';
      case 'medium': return 'primary';
      case 'high': return 'secondary';
      case 'urgent': return 'secondary';
      default: return 'default';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'low': return 'Baixa';
      case 'medium': return 'Média';
      case 'high': return 'Alta';
      case 'urgent': return 'Urgente';
      default: return priority;
    }
  };

  // Função para verificar se todos os campos obrigatórios estão preenchidos
  const isFormValid = () => {
    return formData.title.trim() && formData.dueDate;
  };

  // Função para obter mensagem de status dos campos obrigatórios
  const getFormStatusMessage = () => {
    const missing = [];
    if (!formData.title.trim()) missing.push('Título');
    if (!formData.dueDate) missing.push('Data');
    
    if (missing.length === 0) {
      return { message: '✅ Todos os campos obrigatórios preenchidos', color: 'success' };
    } else {
      return { 
        message: `⚠️ Faltam: ${missing.join(', ')}`, 
        color: 'warning' 
      };
    }
  };

  return (
    <>
      {/* Dialog Principal */}
      <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth={false}
      fullWidth
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">
          <TaskIcon style={{ marginRight: 6, fontSize: '1.2rem' }} />
          {task ? 'Editar Tarefa' : 'Nova Tarefa'}
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.tabsContainer}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <TaskIcon />
                  <span>Tarefa Principal</span>
                </Box>
              }
              style={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Badge badgeContent={subtasks.length} color="primary" className={classes.tabBadge}>
                    <SubtaskIcon />
                  </Badge>
                  <span>Subtarefas</span>
                </Box>
              }
              style={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Badge badgeContent={comments.length} color="primary" className={classes.tabBadge}>
                    <CommentIcon />
                  </Badge>
                  <span>Comentários</span>
                </Box>
              }
              style={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Badge badgeContent={attachments.length} color="primary" className={classes.tabBadge}>
                    <AttachIcon />
                  </Badge>
                  <span>Anexos</span>
                </Box>
              }
              style={{ textTransform: 'none', fontWeight: 600 }}
            />
          </Tabs>
        </Box>

        {/* ABA 1: Tarefa Principal */}
        <TabPanel value={tabValue} index={0} className={classes.tabPanel}>
          {/* Informação sobre campos obrigatórios */}
          <Box className={classes.requiredInfoBox}>
            <Typography variant="body2" className={classes.requiredInfoTitle}>
              📋 <strong>Campos Obrigatórios:</strong> Título e Data de Vencimento
            </Typography>
            <Typography variant="caption" className={classes.requiredInfoHint}>
              A categoria é opcional. 🕐 A hora é opcional.
            </Typography>
            <Typography
              variant="caption"
              className={`${classes.requiredInfoStatus} ${getFormStatusMessage().color === 'success' ? 'success' : 'warning'}`}
            >
              {getFormStatusMessage().message}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Título da Tarefa *"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                variant="outlined"
                className={classes.styledTextField}
                placeholder="Digite o título da tarefa..."
                error={validationErrors.title}
                helperText={validationErrors.title ? 'Título é obrigatório' : ''}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                variant="outlined"
                className={classes.styledTextField}
                placeholder="Descreva os detalhes da tarefa..."
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth variant="outlined" className={classes.styledSelect}>
                <InputLabel>Prioridade</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  label="Prioridade"
                >
                  <MenuItem value="low">🟢 Baixa</MenuItem>
                  <MenuItem value="medium">🟡 Média</MenuItem>
                  <MenuItem value="high">🔴 Alta</MenuItem>
                  <MenuItem value="urgent">🚨 Urgente</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" className={classes.styledSelect}>
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  label="Categoria"
                >
                  <MenuItem value="">
                    <em>Nenhuma categoria</em>
                  </MenuItem>
                  
                  {/* 🏢 DEPARTAMENTAIS */}
                  <ListSubheader>🏢 Departamentais</ListSubheader>
                  <MenuItem value="administrativo">🏢 Administrativo</MenuItem>
                  <MenuItem value="financeiro">💰 Financeiro</MenuItem>
                  <MenuItem value="comercial">🤝 Comercial</MenuItem>
                  <MenuItem value="marketing">📢 Marketing</MenuItem>
                  <MenuItem value="atendimento">📞 Atendimento</MenuItem>
                  <MenuItem value="recursos-humanos">👥 Recursos Humanos</MenuItem>
                  <MenuItem value="tecnologia">💻 Tecnologia</MenuItem>
                  <MenuItem value="juridico">⚖️ Jurídico</MenuItem>
                  
                  {/* 📋 ATIVIDADES */}
                  <ListSubheader>📋 Atividades</ListSubheader>
                  <MenuItem value="reuniao">🤝 Reunião</MenuItem>
                  <MenuItem value="projeto">📋 Projeto</MenuItem>
                  <MenuItem value="treinamento">🎓 Treinamento</MenuItem>
                  <MenuItem value="apresentacao">📊 Apresentação</MenuItem>
                  <MenuItem value="documentacao">📄 Documentação</MenuItem>
                  <MenuItem value="analise">🔍 Análise</MenuItem>
                  <MenuItem value="planejamento">📅 Planejamento</MenuItem>
                  <MenuItem value="revisao">✅ Revisão</MenuItem>
                  
                  {/* 🎯 ESPECIAIS */}
                  <ListSubheader>🎯 Especiais</ListSubheader>
                  <MenuItem value="urgente">🚨 Urgente</MenuItem>
                  <MenuItem value="follow-up">🔄 Follow-up</MenuItem>
                  <MenuItem value="pessoal">👤 Pessoal</MenuItem>
                  <MenuItem value="manutencao">🔧 Manutenção</MenuItem>
                  <MenuItem value="auditoria">🔎 Auditoria</MenuItem>
                  <MenuItem value="compliance">📜 Compliance</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Data e Hora em uma única linha */}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="📅 Data de Vencimento *"
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleInputChange('dueDate', e.target.value)}
               
               
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                className={classes.styledTextField}
                error={validationErrors.dueDate}
                helperText={validationErrors.dueDate ? 'Data obrigatória' : ''}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="🕐 Hora"
                type="time"
                value={formData.dueTime}
                onChange={(e) => handleInputChange('dueTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                className={classes.styledTextField}
                inputProps={{
                  step: 300, // 5 minutos
                }}
              />
            </Grid>
            {/* Responsáveis (bloqueado quando pública) */}
            <Grid item xs={12}>
              <Box className={classes.recurrenceSection}>
                <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={1}>
                  <Box display="flex" alignItems="center">
                    <Avatar style={{ width: 24, height: 24, marginRight: 8 }} />
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>Responsáveis</Typography>
                  </Box>
                </Box>
                <Autocomplete
                  multiple
                  options={allUsers || []}
                  getOptionLabel={(option) => option.name || ''}
                  value={assignees}
                  onChange={(e, value) => setAssignees(value)}
                  disableClearable={false}
                  disabled={formData.isPublic}
                  renderOption={(option) => (
                    <Box display="flex" alignItems="center">
                      <Avatar style={{ width: 28, height: 28, marginRight: 8 }}>
                        {(option.name || '?').slice(0,1).toUpperCase()}
                      </Avatar>
                      <span>{option.name}</span>
                    </Box>
                  )}
                  renderTags={(value, getTagProps) => value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      avatar={<Avatar>{(option.name || '?').slice(0,1).toUpperCase()}</Avatar>}
                      label={option.name}
                      color="primary"
                      size="small"
                    />
                  ))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={formData.isPublic ? "Tarefa pública não permite responsáveis" : "Adicionar responsáveis"}
                      placeholder={formData.isPublic ? "Pública" : "Buscar usuários..."}
                      className={classes.styledTextField}
                    />
                  )}
                />
                {formData.isPublic && (
                  <Typography variant="caption" color="textSecondary" style={{ marginTop: 6, display: 'block' }}>
                    Como a tarefa é pública, não é possível definir responsáveis.
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Primeira linha: Cards simples (Visibilidade e Notificações) */}
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            {/* Seção de Visibilidade */}
            <Grid item xs={12} md={6}>
              <Box className={classes.recurrenceSection}>
                <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={1}>
                  <Box display="flex" alignItems="center">
                    <CategoryIcon style={{ marginRight: 8, color: theme.palette.primary.main, fontSize: '1.1rem' }} />
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>Visibilidade</Typography>
                  </Box>
                  <Switch
                    checked={formData.isPublic}
                    onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                    color="primary"
                    size="small"
                  />
                </Box>

                <Typography variant="body2" style={{ marginTop: 8 }}>
                  {formData.isPublic ? 'Tarefa pública' : 'Tarefa privada'}
                </Typography>
                
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                  {formData.isPublic 
                    ? "⚠️ Visível para todos da empresa" 
                    : "🔒 Visível apenas para você"
                  }
                </Typography>
              </Box>
            </Grid>

            {/* Seção de Notificações WhatsApp */}
            <Grid item xs={12} md={6}>
              <Box className={classes.recurrenceSection}>
                <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={1}>
                  <Box display="flex" alignItems="center">
                    <WhatsAppIcon style={{ marginRight: 8, color: '#25D366', fontSize: '1.1rem' }} />
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>Notificações</Typography>
                  </Box>
                  <Switch
                    checked={formData.notificationsEnabled}
                    onChange={(e) => handleInputChange('notificationsEnabled', e.target.checked)}
                    color="primary"
                    size="small"
                  />
                </Box>

                <Typography variant="body2" style={{ marginTop: 8 }}>
                  {formData.notificationsEnabled ? 'Notificações ativadas' : 'Notificações desativadas'}
                </Typography>
                
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                  {formData.notificationsEnabled 
                    ? "✅ Você receberá alertas automáticos via WhatsApp" 
                    : "🔕 Não receberá notificações desta tarefa"
                  }
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Segunda linha: Cards expandidos (Lembrete e Recorrência) */}
          <Grid container spacing={2}>
            {/* Seção de Lembrete Personalizado */}
            <Grid item xs={12} md={6}>
              <Box className={classes.reminderSection}>
                <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={1}>
                  <Box display="flex" alignItems="center">
                    <span style={{ marginRight: 8, fontSize: '1.1rem' }}>🔔</span>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>Lembrete Personalizado</Typography>
                  </Box>
                  <Switch
                    checked={formData.reminderEnabled}
                    onChange={(e) => handleInputChange('reminderEnabled', e.target.checked)}
                    color="primary"
                    size="small"
                  />
                </Box>

                {formData.reminderEnabled && (
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="📅 Data do Lembrete"
                        type="date"
                        value={formData.reminderDate}
                        onChange={(e) => handleInputChange('reminderDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        size="small"
                        className={classes.styledTextField}
                        helperText="Quando você quer ser lembrado"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="🕐 Hora do Lembrete"
                        type="time"
                        value={formData.reminderTime}
                        onChange={(e) => handleInputChange('reminderTime', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        size="small"
                        className={classes.styledTextField}
                        helperText="Horário da notificação"
                        inputProps={{
                          step: 300, // 5 minutos
                        }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Grid>
            
            {/* Seção de Recorrência */}
            <Grid item xs={12} md={6}>
              <Box className={classes.recurrenceCardSection}>
                <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={1}>
                  <Box display="flex" alignItems="center">
                    <RepeatIcon style={{ marginRight: 8, color: theme.palette.primary.main, fontSize: '1.1rem' }} />
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>🔄 Recorrência</Typography>
                  </Box>
                  <Switch
                    checked={formData.isRecurring}
                    onChange={(e) => handleInputChange('isRecurring', e.target.checked)}
                    color="primary"
                    size="small"
                  />
                </Box>

                {formData.isRecurring && (
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <FormControl fullWidth variant="outlined" className={classes.styledSelect} size="small">
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          value={formData.recurrenceType}
                          onChange={(e) => handleInputChange('recurrenceType', e.target.value)}
                          label="Tipo"
                        >
                          <MenuItem value="daily">📅 Diária</MenuItem>
                          <MenuItem value="weekly">📆 Semanal</MenuItem>
                          <MenuItem value="monthly">🗓️ Mensal</MenuItem>
                          <MenuItem value="yearly">📋 Anual</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Intervalo"
                        type="number"
                        value={formData.recurrenceInterval}
                        onChange={(e) => handleInputChange('recurrenceInterval', parseInt(e.target.value))}
                        variant="outlined"
                        className={classes.styledTextField}
                        inputProps={{ min: 1 }}
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Repetições"
                        type="number"
                        value={formData.recurrenceCount}
                        onChange={(e) => handleInputChange('recurrenceCount', e.target.value)}
                        variant="outlined"
                        className={classes.styledTextField}
                        inputProps={{ min: 1 }}
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Data Final"
                        type="date"
                        value={formData.recurrenceEndDate}
                        onChange={(e) => handleInputChange('recurrenceEndDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        className={classes.styledTextField}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        {/* ABA 2: Subtarefas */}
        <TabPanel value={tabValue} index={1} className={classes.tabPanel}>
          {/* Formulário para nova subtarefa */}
          <Card className={classes.styledCard}>
            <CardContent>
              <Typography variant="h6" gutterBottom className={classes.addFormTitle}>
                ➕ Adicionar Nova Subtarefa
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Título da Subtarefa"
                    value={newSubtask.title}
                    onChange={(e) => setNewSubtask(prev => ({ ...prev, title: e.target.value }))}
                    variant="outlined"
                    className={classes.styledTextField}
                    placeholder="Digite o título..."
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined" className={classes.styledSelect}>
                    <InputLabel>Prioridade</InputLabel>
                    <Select
                      value={newSubtask.priority}
                      onChange={(e) => setNewSubtask(prev => ({ ...prev, priority: e.target.value }))}
                      label="Prioridade"
                    >
                      <MenuItem value="low">🟢 Baixa</MenuItem>
                      <MenuItem value="medium">🟡 Média</MenuItem>
                      <MenuItem value="high">🔴 Alta</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Vencimento"
                    type="date"
                    value={newSubtask.dueDate}
                    onChange={(e) => setNewSubtask(prev => ({ ...prev, dueDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    className={classes.styledTextField}
                  />
                </Grid>
              </Grid>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                className={classes.addButton}
                startIcon={<AddIcon />}
                onClick={handleAddSubtask}
                fullWidth
              >
                Adicionar Subtarefa
              </Button>
            </CardActions>
          </Card>

          {/* Lista de subtarefas */}
          <Box>
            {subtasks.map((subtask, index) => {
              const isPendingDelete = pendingSubtaskDeletes.includes(subtask.id);
              return (
                <Card
                  key={index}
                  className={`${classes.styledCard} ${isPendingDelete ? classes.pendingDeleteCard : ''} ${subtask.status === 'completed' ? classes.completedSubtask : ''}`}
                >
                  <CardContent className={isPendingDelete ? classes.pendingDeleteContent : ''}>
                    {isPendingDelete && (
                      <Box mb={1}>
                        <Chip
                          icon={<WarningIcon />}
                          label="🗑️ Será excluído ao salvar a tarefa"
                          className={classes.deleteWarningChip}
                          size="small"
                        />
                      </Box>
                    )}
                    {editingSubtask === index ? (
                      // Modo de edição
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Título da Subtarefa"
                            value={subtask.title}
                            onChange={(e) => handleSubtaskFieldChange(index, 'title', e.target.value)}
                            variant="outlined"
                            className={classes.styledTextField}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl fullWidth variant="outlined" className={classes.styledSelect} size="small">
                            <InputLabel>Prioridade</InputLabel>
                            <Select
                              value={subtask.priority}
                              onChange={(e) => handleSubtaskFieldChange(index, 'priority', e.target.value)}
                              label="Prioridade"
                            >
                              <MenuItem value="low">🟢 Baixa</MenuItem>
                              <MenuItem value="medium">🟡 Média</MenuItem>
                              <MenuItem value="high">🔴 Alta</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Vencimento"
                            type="date"
                            value={subtask.dueDate}
                            onChange={(e) => handleSubtaskFieldChange(index, 'dueDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            className={classes.styledTextField}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" gap={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<SaveIcon />}
                              onClick={() => handleSaveEditSubtask(index, subtask)}
                            >
                              Salvar
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CancelIcon />}
                              onClick={handleCancelEditSubtask}
                            >
                              Cancelar
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    ) : (
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                          <Box display="flex" flexDirection="column" flex={1}>
                            <Typography 
                              variant="subtitle2" 
                              style={{ fontWeight: 600, textDecoration: subtask.status === 'completed' ? 'line-through' : 'none', opacity: subtask.status === 'completed' ? 0.7 : 1 }}
                            >
                              {subtask.title || 'Sem título'}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Chip
                                label={getPriorityLabel(subtask.priority)}
                                className={`${classes.priorityBadge} ${subtask.priority}`}
                                size="small"
                              />
                              {subtask.dueDate && (() => {
                                try {
                                  const date = new Date(subtask.dueDate);
                                  if (!isNaN(date.getTime())) {
                                    return (
                                      <Chip
                                        icon={<EventIcon />}
                                        label={format(date, 'dd/MM/yyyy')}
                                        size="small"
                                        variant="outlined"
                                      />
                                    );
                                  }
                                } catch (error) {
                                  console.warn('Erro ao formatar data da subtarefa:', subtask.dueDate, error);
                                }
                                return null;
                              })()}
                            </Box>
                            {subtask.status === 'completed' && (
                              <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                                Concluída em {(() => {
                                  try {
                                    const dateStr = subtask.completedAt || subtask.updatedAt;
                                    const d = dateStr ? new Date(dateStr) : null;
                                    return d && !isNaN(d.getTime()) ? format(d, 'dd/MM/yyyy HH:mm') : '-';
                                  } catch {
                                    return '-';
                                  }
                                })()}
                              </Typography>
                            )}
                            {/* Informações do usuário e data */}
                            {(subtask.user || subtask.createdAt) && (
                              <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                                {subtask.createdAt && (
                                  <span>Adicionada em {format(new Date(subtask.createdAt), 'dd/MM/yyyy HH:mm')}</span>
                                )}
                                {subtask.user && (
                                  <span style={{ color: '#1976d2', fontWeight: 500 }}> por {subtask.user.name}</span>
                                )}
                              </Typography>
                            )}
                          </Box>
                          <Box display="flex" gap={1}>
                            <Tooltip title={subtask.status === 'completed' ? 'Marcar como pendente' : 'Marcar como concluída'}>
                              <IconButton
                                onClick={() => handleToggleSubtaskStatus(index)}
                                size="small"
                                disabled={isPendingDelete}
                                color={subtask.status === 'completed' ? 'default' : 'primary'}
                              >
                                {subtask.status === 'completed' ? <UndoIcon /> : <CheckIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar subtarefa">
                              <IconButton
                                color="primary"
                                onClick={() => handleEditSubtask(index)}
                                size="small"
                                disabled={isPendingDelete || subtask.status === 'completed'}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={isPendingDelete ? 'Cancelar exclusão' : 'Excluir subtarefa'}>
                              <IconButton
                                color="secondary"
                                onClick={() =>
                                  isPendingDelete
                                    ? (() => {
                                        setPendingSubtaskDeletes(prev => prev.filter(id => id !== subtask.id));
                                        toast.info('Exclusão cancelada!');
                                      })()
                                    : handleRemoveSubtask(index)
                                }
                                size="small"
                                disabled={!isPendingDelete && subtask.status === 'completed'}
                              >
                                {isPendingDelete ? <CancelIcon /> : <DeleteIcon />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            {subtasks.length === 0 && (
              <Card className={classes.styledCard}>
                <CardContent style={{ textAlign: 'center', padding: 40 }}>
                  <SubtaskIcon style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
                  <Typography variant="body1" color="textSecondary">
                    Nenhuma subtarefa adicionada ainda
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Use o formulário acima para adicionar subtarefas
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </TabPanel>

        {/* ABA 3: Comentários */}
        <TabPanel value={tabValue} index={2} className={classes.tabPanel}>
          {/* Formulário para novo comentário */}
          <Card className={classes.styledCard}>
            <CardContent>
              <Typography variant="h6" gutterBottom className={classes.addFormTitle}>
                ➕ Adicionar Comentário
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  placeholder="Digite seu comentário..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  variant="outlined"
                  className={classes.styledTextField}
                  multiline
                  rows={2}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleAddComment()}
                />
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    className={classes.sendCommentButton}
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                  >
                    Enviar
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Lista de comentários */}
          <Box>
            {comments.map((comment, index) => {
              const isPendingDelete = pendingCommentDeletes.includes(comment.id);
              return (
              <Card 
                key={index} 
                className={`${classes.commentCard} ${isPendingDelete ? classes.pendingDeleteCard : ''}`}
              >
                <CardContent className={isPendingDelete ? classes.pendingDeleteContent : ''}>
                  {isPendingDelete && (
                    <Box mb={1}>
                      <Chip
                        icon={<WarningIcon />}
                        label="🗑️ Será excluído ao salvar a tarefa"
                        className={classes.deleteWarningChip}
                        size="small"
                      />
                    </Box>
                  )}
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <Avatar style={{ background: 'linear-gradient(45deg, #667eea, #764ba2)' }}>
                      {comment.user?.name?.charAt(0) || 'U'}
                    </Avatar>
                    <Box flex={1}>
                      {editingComment === index ? (
                        // Modo de edição
                        <Box>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={comment.content}
                            onChange={(e) => handleCommentFieldChange(index, e.target.value)}
                            variant="outlined"
                            className={classes.styledTextField}
                            size="small"
                            placeholder="Edite o comentário..."
                          />
                          <Box display="flex" gap={1} justifyContent="flex-end" marginTop={1}>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<SaveIcon />}
                              onClick={() => handleSaveEditComment(index)}
                            >
                              Salvar
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CancelIcon />}
                              onClick={handleCancelEditComment}
                            >
                              Cancelar
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        // Modo de visualização
                        <Box>
                          <Typography variant="body1" style={{ marginBottom: 8 }}>
                            {comment.content}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            <strong>{comment.user?.name}</strong> • {format(new Date(comment.createdAt), 'dd/MM/yyyy HH:mm')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {editingComment !== index && (
                      <Box display="flex" gap={1}>
                        <Tooltip title="Editar comentário">
                          <IconButton
                            color="primary"
                            onClick={() => handleEditComment(index)}
                            size="small"
                            disabled={isPendingDelete}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isPendingDelete ? "Cancelar exclusão" : "Excluir comentário"}>
                          <IconButton
                            color="secondary"
                            onClick={() => isPendingDelete ? 
                              (() => {
                                setPendingCommentDeletes(prev => prev.filter(id => id !== comment.id));
                                toast.info('Exclusão cancelada!');
                              })() : 
                              handleRemoveComment(index)
                            }
                            size="small"
                          >
                            {isPendingDelete ? <CancelIcon /> : <DeleteIcon />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
            })}
            {comments.length === 0 && (
              <Card className={classes.styledCard}>
                <CardContent style={{ textAlign: 'center', padding: 40 }}>
                  <CommentIcon style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
                  <Typography variant="body1" color="textSecondary">
                    Nenhum comentário adicionado ainda
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Adicione comentários para colaborar na tarefa
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </TabPanel>

        {/* ABA 4: Anexos */}
        <TabPanel value={tabValue} index={3} className={classes.tabPanel}>
          {/* Área de upload */}
          <input
            accept="*/*"
            style={{ display: 'none' }}
            id="file-upload"
            type="file"
            multiple
            onChange={handleFileUpload}
          />
          <label htmlFor="file-upload">
            <Box className={classes.uploadArea}>
              <UploadIcon style={{ fontSize: 48, marginBottom: 16, color: theme.palette.primary.main }} />
              <Typography variant="h6" color="primary" gutterBottom>
                Clique para anexar arquivos
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Ou arraste e solte arquivos aqui
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                Suporte para todos os tipos de arquivo
              </Typography>
            </Box>
          </label>

          {/* Lista de anexos */}
          <Box>
            {attachments.map((attachment, index) => {
              const isPendingDelete = pendingAttachmentDeletes.includes(attachment.id);
              return (
              <Card 
                key={index} 
                className={`${classes.attachmentCard} ${isPendingDelete ? classes.pendingDeleteCard : ''}`}
              >
                <CardContent className={isPendingDelete ? classes.pendingDeleteContent : ''}>
                  {isPendingDelete && (
                    <Box mb={1}>
                      <Chip
                        icon={<WarningIcon />}
                        label="🗑️ Será excluído ao salvar a tarefa"
                        className={classes.deleteWarningChip}
                        size="small"
                      />
                    </Box>
                  )}
                  <Box display="flex" alignItems="center" gap={2}>
                    {getFileIcon(attachment.originalName, attachment.mimetype)}
                    <Box flex={1}>
                      <Typography variant="body1" style={{ fontWeight: 600 }}>
                        {attachment.originalName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {formatFileSize(attachment.size)}
                        {attachment.createdAt && (
                          <span> • Adicionado em {format(new Date(attachment.createdAt), 'dd/MM/yyyy HH:mm')}</span>
                        )}
                        {attachment.user && (
                          <span style={{ color: '#1976d2', fontWeight: 500 }}> por {attachment.user.name}</span>
                        )}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      {(attachment.id || attachment.url) && !isPendingDelete && (
                        <>
                          {/* Botão Visualizar - só para mídia */}
                          {isViewableMedia(attachment.originalName, attachment.mimetype) && (
                            <Tooltip title="Visualizar">
                              <IconButton
                                color="primary"
                                onClick={() => handleViewAttachment(attachment)}
                                size="small"
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* Botão Download - para todos os tipos */}
                          <Tooltip title="Download">
                            <IconButton
                              color="primary"
                              onClick={() => handleDownloadAttachment(attachment)}
                              size="small"
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title={isPendingDelete ? "Cancelar exclusão" : "Remover anexo"}>
                        <IconButton
                          color="secondary"
                          onClick={() => isPendingDelete ? 
                            (() => {
                              setPendingAttachmentDeletes(prev => prev.filter(id => id !== attachment.id));
                              toast.info('Exclusão cancelada!');
                            })() : 
                            handleRemoveAttachment(attachment.id)
                          }
                          size="small"
                        >
                          {isPendingDelete ? <CancelIcon /> : <DeleteIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
            })}
            {attachments.length === 0 && (
              <Card className={classes.styledCard}>
                <CardContent style={{ textAlign: 'center', padding: 40 }}>
                  <AttachIcon style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
                  <Typography variant="body1" color="textSecondary">
                    Nenhum anexo adicionado ainda
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Use a área de upload acima para anexar arquivos
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions className={classes.actionButtons}>
        <Button onClick={onClose} disabled={loading} variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : (task ? <TaskIcon /> : <AddIcon />)}
          style={{
            opacity: !loading && !isFormValid() ? 0.7 : 1,
            transform: !loading && isFormValid() ? 'scale(1.02)' : 'scale(1)',
            transition: 'all 0.2s ease'
          }}
        >
          {loading ? 'Salvando...' : (task ? 'Atualizar Tarefa' : 'Criar Tarefa')}
        </Button>
      </DialogActions>

      {/* Modal de confirmação de exclusão */}
      <Dialog 
        open={confirmDeleteOpen} 
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        className={classes.confirmDialog}
      >
        <DialogTitle className={classes.confirmDialogTitle}>
          <WarningIcon style={{ fontSize: '1.5rem' }} />
          ⚠️ Confirmação de Exclusão
        </DialogTitle>
        <DialogContent style={{ padding: '24px' }}>
          <Typography variant="h6" style={{ marginBottom: 16, color: '#d32f2f', fontWeight: 600 }}>
            {deleteMessage}
          </Typography>
          <Box 
            style={{ 
              background: 'linear-gradient(135deg, #ffebee, #fce4ec)', 
              padding: '16px', 
              borderRadius: '12px',
              border: '1px solid #ffcdd2',
              marginBottom: '16px'
            }}
          >
            <Typography variant="body2" style={{ color: '#d32f2f', fontWeight: 500 }}>
              🚨 <strong>Atenção:</strong> O item ficará marcado para exclusão e será removido apenas quando você salvar a tarefa.
            </Typography>
            <Typography variant="body2" style={{ color: '#666', marginTop: 8, display: 'block' }}>
              Você pode cancelar esta ação clicando no botão de cancelar exclusão antes de salvar.
            </Typography>
          </Box>
          
          <Typography variant="body2" style={{ color: '#666', fontStyle: 'italic' }}>
            💡 O item permanecerá na lista com indicador visual até ser definitivamente excluído.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px 24px' }}>
          <Button 
            onClick={handleCancelDelete} 
            variant="outlined"
            style={{
              borderRadius: 25,
              padding: '8px 24px',
              borderColor: '#666',
              color: '#666',
              fontWeight: 600
            }}
          >
            ✖️ Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            className={classes.confirmDeleteButton}
            startIcon={<DeleteIcon />}
          >
            🗑️ Confirmar Exclusão
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>

    {/* Modal de Visualização de Mídia */}
    <Dialog
      open={mediaViewerOpen}
      onClose={() => {
        setMediaViewerOpen(false);
        setMediaLoading(false);
        setCurrentMedia(null);
      }}
      maxWidth="lg"
      fullWidth
      className={classes.mediaViewerDialog}
    >
      <DialogTitle 
        style={{ 
          color: 'white', 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Typography variant="h6" style={{ color: 'white' }}>
          {currentMedia?.name}
        </Typography>
        <IconButton 
          onClick={() => {
            setMediaViewerOpen(false);
            setMediaLoading(false);
            setCurrentMedia(null);
          }} 
          style={{ color: 'white' }}
        >
          <CancelIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent className={classes.mediaViewerContent}>
        {mediaLoading && (
          <Box 
            style={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              zIndex: 10
            }}
          >
            <CircularProgress style={{ color: 'white' }} />
            <Typography style={{ color: 'white' }}>
              Carregando {currentMedia?.name}...
            </Typography>
          </Box>
        )}
        
        {currentMedia && (
          <>
            {currentMedia.type === 'image' && (
              <img
                src={currentMedia.url}
                alt={currentMedia.name}
                className={classes.mediaElement}
                onError={(e) => {
                  console.error('[ATTACHMENT VIEW] Erro ao carregar imagem:', {
                    url: currentMedia.url,
                    name: currentMedia.name,
                    error: e
                  });
                  
                  // Tentar URLs alternativas se disponíveis
                  if (currentMedia.fallbackUrls && currentMedia.fallbackUrls.length > 0) {
                    const nextUrl = currentMedia.fallbackUrls[0];
                    console.log('[ATTACHMENT VIEW] Tentando URL alternativa:', nextUrl);
                    
                    // Atualizar o currentMedia com a próxima URL
                    setCurrentMedia(prev => ({
                      ...prev,
                      url: nextUrl,
                      fallbackUrls: prev.fallbackUrls.slice(1)
                    }));
                  } else {
                    console.error('[ATTACHMENT VIEW] Todas as URLs falharam');
                    toast.error(`Não foi possível carregar a imagem: ${currentMedia.name}`);
                    setMediaViewerOpen(false);
                  }
                }}
                onLoad={() => {
                  console.log('[ATTACHMENT VIEW] Imagem carregada com sucesso:', currentMedia.name);
                  setMediaLoading(false);
                }}
              />
            )}
            
            {currentMedia.type === 'video' && (
              <video
                src={currentMedia.url}
                controls
                className={classes.mediaElement}
                onError={(e) => {
                  console.error('[ATTACHMENT VIEW] Erro ao carregar vídeo:', {
                    url: currentMedia.url,
                    name: currentMedia.name,
                    error: e
                  });
                  
                  // Tentar URLs alternativas se disponíveis
                  if (currentMedia.fallbackUrls && currentMedia.fallbackUrls.length > 0) {
                    const nextUrl = currentMedia.fallbackUrls[0];
                    console.log('[ATTACHMENT VIEW] Tentando URL alternativa para vídeo:', nextUrl);
                    
                    setCurrentMedia(prev => ({
                      ...prev,
                      url: nextUrl,
                      fallbackUrls: prev.fallbackUrls.slice(1)
                    }));
                  } else {
                    toast.error(`Não foi possível carregar o vídeo: ${currentMedia.name}`);
                    setMediaViewerOpen(false);
                  }
                }}
                onLoadedData={() => {
                  console.log('[ATTACHMENT VIEW] Vídeo carregado com sucesso:', currentMedia.name);
                  setMediaLoading(false);
                }}
              >
                Seu navegador não suporta a tag de vídeo.
              </video>
            )}
            
            {currentMedia.type === 'audio' && (
              <Box className={classes.audioContainer}>
                <AudioIcon style={{ fontSize: 64, color: 'white' }} />
                <Typography variant="h6" style={{ color: 'white' }}>
                  {currentMedia.name}
                </Typography>
                <audio
                  src={currentMedia.url}
                  controls
                  style={{
                    width: '100%',
                    maxWidth: '500px'
                  }}
                  onError={(e) => {
                    console.error('[ATTACHMENT VIEW] Erro ao carregar áudio:', {
                      url: currentMedia.url,
                      name: currentMedia.name,
                      error: e
                    });
                    
                    // Tentar URLs alternativas se disponíveis
                    if (currentMedia.fallbackUrls && currentMedia.fallbackUrls.length > 0) {
                      const nextUrl = currentMedia.fallbackUrls[0];
                      console.log('[ATTACHMENT VIEW] Tentando URL alternativa para áudio:', nextUrl);
                      
                      setCurrentMedia(prev => ({
                        ...prev,
                        url: nextUrl,
                        fallbackUrls: prev.fallbackUrls.slice(1)
                      }));
                    } else {
                      toast.error(`Não foi possível carregar o áudio: ${currentMedia.name}`);
                    }
                  }}
                  onLoadedData={() => {
                    console.log('[ATTACHMENT VIEW] Áudio carregado com sucesso:', currentMedia.name);
                    setMediaLoading(false);
                  }}
                >
                  Seu navegador não suporta a tag de áudio.
                </audio>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Button 
          onClick={() => {
            setMediaViewerOpen(false);
            setMediaLoading(false);
            setCurrentMedia(null);
          }} 
          style={{ color: 'white' }}
        >
          Fechar
        </Button>
        {currentMedia && (
          <Button 
            onClick={() => {
              // Encontrar o anexo correspondente para download
              const attachment = attachments.find(att => 
                att.originalName === currentMedia.name
              );
              if (attachment) {
                handleDownloadAttachment(attachment);
              }
            }}
            startIcon={<DownloadIcon />}
            style={{ color: 'white' }}
          >
            Download
          </Button>
        )}
      </DialogActions>
    </Dialog>
    </>
  );
};

export default TaskFormModal;
